import type { Expr, Program, ValueType } from '../ir.js';
import { identifierMap, repeatCounters } from '../naming.js';
import { typeOf } from '../types.js';

export interface GenContext {
  program: Program;
  /** Identifier for a block variable name. */
  id: (name: string) => string;
  /** Hidden counter name for a "repeat n times" statement. */
  counter: (repeatId: string) => string;
  type: (e: Expr) => ValueType;
}

export function createContext(program: Program): GenContext {
  const ids = identifierMap(program);
  const counters = repeatCounters(program, ids);
  return {
    program,
    id: (name) => ids.get(name) ?? name,
    counter: (repeatId) => counters.get(repeatId) ?? 'k',
    type: (e) => typeOf(e, program.types),
  };
}

/** A generated expression and its precedence (higher binds tighter). */
export interface Code {
  code: string;
  prec: number;
}

export const ATOM = 100;

export function wrap(c: Code, min: number): string {
  return c.prec < min ? `(${c.code})` : c.code;
}

/** Number literal as written in code: integers plainly, fractions with a dot. */
export function numberLiteral(n: number): string {
  return Number.isInteger(n) ? n.toFixed(0) : String(n);
}

/** Double-quoted string literal valid in C++, Python and JavaScript. */
export function stringLiteral(s: string): string {
  return JSON.stringify(s);
}

/** Flatten nested joins into their parts, left to right. */
export function joinParts(e: Expr): Expr[] {
  return e.k === 'join' ? [...joinParts(e.left), ...joinParts(e.right)] : [e];
}

/** `x + 3` → [x, 3], `x - 2` → [x, -2], anything else → [e, 0]. */
export function splitOffset(e: Expr): [Expr, number] {
  if (e.k === 'arith' && (e.op === '+' || e.op === '-') && e.right.k === 'num') {
    return [e.left, e.op === '+' ? e.right.value : -e.right.value];
  }
  return [e, 0];
}

/**
 * The end of a slice, `from + len`. Python and JavaScript write a piece of text by where it
 * starts and where it stops; the blocks say how long it is. Numbers are folded so the text reads
 * the way a person writes it: `s[1:3]`, `s[i:i + 2]`, `s[n - 2:n]`. `prec` is the language's
 * precedence for `+`.
 */
export function sliceEnd(from: Expr, len: Expr, code: (e: Expr) => Code, prec: number): string {
  if (from.k === 'num' && len.k === 'num') return numberLiteral(from.value + len.value);
  if (len.k === 'num') {
    const [base, k] = splitOffset(from);
    const at = k + len.value;
    if (at === 0) return code(base).code;
    return `${wrap(code(base), prec)} ${at > 0 ? '+' : '-'} ${numberLiteral(Math.abs(at))}`;
  }
  return `${wrap(code(from), prec)} + ${wrap(code(len), prec + 1)}`;
}

/** Placeholder shown for an empty socket; the program cannot run until it is filled. */
export const MISSING = '?';
